import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { FormGroup, FormArray, FormBuilder } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { Pagination, DtoClientAccount, UserRoles } from '../../../models/mat-account.model';
import { DtoSchool, DtoSchoolAdmin, DtoSchoolUser } from '../../../models/school.model';
import { Users } from '../../../models/user-center.model';
import { SharedService } from '../../../shared/services/shared.service';
import { HelperMethods } from '../../common/helper-methods.service';
import { MatAccountComponent } from '../../mat-account/mat-account.component';
import { MatAccountService } from '../../mat-account/mat-account.service';
import { SchoolService } from '../school.service';
import { Tabs } from '../../package-center/package-center.component';
import { RoleMaster } from '../../user-center/user-role/user-role.component';
import { Role, RolePayload } from '../../../models/role.model';
import { DocumentType } from '../../../const';
import { PackageService } from '../../package-center/package/package.service';

interface UploadFileList {
  id: string | null;
  files: File[];
}

@Component({
  selector: 'app-create-school',
  templateUrl: './create-school.component.html'
})
export class CreateSchoolComponent implements OnInit, OnDestroy {
  @Input() buttonTitle: string = 'Upload from Device';
  @Output() hideHeader = new EventEmitter<boolean>();
  @Output() schoolCreated = new EventEmitter<boolean>();
  @Output() fileChange = new EventEmitter<FileList>();
  @ViewChild('fileInput') fileInput: ElementRef | undefined;
  @Input() packageToAssign: number = 0; //Send PackageId
  @Input() clientAccountId: number = 0;
  private schoolFormSubscription: Subscription | undefined;

  subscription: Subscription = new Subscription();
  steps = Steps; // Enum to define the total number of steps
  currentStep: number = this.steps.AddSchool; // Property to define the current step in independent school creation
  createIndividualSchoolForm: boolean = true; // Property to define whether to show/hide the back/cancel/next button
  showForm: boolean = false; //Property to define whether 'Create Package' is triggered
  openedPackage: boolean = false; //Property to define whether 'Create Package/Edit Package' is triggered
  schoolToEdit: DtoSchool = {} as DtoSchool;
  activeTab: string = Tabs.Package;
  accountAdminList: Users[] = [];
  schoolList: DtoSchool[] = [];
  phoneNumberUtil = PhoneNumberUtil.getInstance();
  role = RoleMaster;
  userRole: string[] = [];
  roleName: string[] = [];
  isEdit: boolean = false;
  totalRecords: number = 0;
  pagination: Pagination = {
    searchTerm: "",
    pageOffset: 0,
    pageSize: 10,
    sortColumn: "",
    sortDirection: "",
    userRole: [] as string[]
  }
  selectedFile: File | null = null;
  schoolUploadFileList: Document[] = [];  
  userEmail: string = "";
  schoolDetailsForm = this.fb.group({
    schools: this.fb.array([])
  });
  roleForm = new FormGroup({
    roleDetails: new FormArray([]),
  });
  completedProcess: boolean = false;
  createdSchoolDetails: DtoSchool[] = [];
  roles: Role[] = [];
  maxStep: number = 0;
  partialSave: boolean = false;
  schoolAccountUploadedFileList: { [index: number]: UploadFileList } = {};
  schoolAdminUploadedFileLists: { [index: number]: UploadFileList } = {};
  schoolUserUploadedFileLists: { [index: string]: UploadFileList } = {};

  constructor(
    private matAccountService: MatAccountService,
    private schoolService: SchoolService,
    private toasterService: ToastrService,
    public helperMethods: HelperMethods,
    public dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<MatAccountComponent>,
    private sharedService: SharedService,
    private fb: FormBuilder,
    private packageService: PackageService
  ) { }
  ngOnInit(): void {
    this.userRole = sessionStorage.getItem('user_role')?.split(',')!;
    this.userRole?.forEach(element => {
      this.roleName.push(this.getRoleNameByValue(RoleMaster, element)!);
    });

    this.userEmail = sessionStorage.getItem('user_email')!;
    this.schoolFormSubscription = this.sharedService.schoolFormValidity.subscribe(schoolForm => {
      this.schoolDetailsForm = schoolForm;
    });

    this.sharedService.roleFormValidity.subscribe(roleForm => {
      this.roleForm = roleForm;
    });
    this.getAccountAdminDetails();
  }

  getAccountAdminDetails() {
    let payload: UserRoles = {} as UserRoles;
    payload.userRole = this.userRole;
    this.matAccountService.getAccountAdminDetails(this.userRole).subscribe((response: Users[] | any) => {
      this.accountAdminList = response;
    }, (err: HttpErrorResponse) => {
      if(err.error){
        this.toasterService.error(err.error, "Error");
      }
    });
  }

  getRoleNameByValue<T extends { [index: string]: string }>(
    enumObj: T,
    value: string
  ): string | undefined {
    const keys = Object.keys(enumObj).filter((x) => enumObj[x] === value);
    return keys.length > 0 ? keys[0] : undefined;
  }

  goToSelectedStep(stepToChange: number) {
    if(stepToChange <= this.maxStep){
      this.currentStep = stepToChange;
      this.maxStep = this.currentStep > this.maxStep ? this.currentStep : this.maxStep;
      this.isEdit = this.setValueForIsEdit(this.currentStep);
    }
  }

  disableNext(currentStep: number){
    switch(currentStep){
      case this.steps.AddSchool:
        if(!this.isFormValidExceptSchoolUsers()){
          return false;
        }
        else{
          return true;
        }
      case this.steps.CreateUser:
        if(this.schoolDetailsForm.invalid || this.schoolDetailsForm.untouched){
          return false;
        }
        else{
          return true;
        }
      default:
        return true;
    }
  }

  setValueForIsEdit(currentStep: number): boolean{
    if(currentStep == this.steps.AddSchool){
      return this.createdSchoolDetails && this.createdSchoolDetails.length > 0 && this.createdSchoolDetails[0].schoolId! > 0;
    }
    else{
      return false;
    }
  }

  async triggerAction(){
    if(!this.disableNext(this.currentStep)){
      this.schoolDetailsForm.markAllAsTouched();
      this.toasterService.warning("Please fill the required fields to proceed");
      return;
    }
    
    let matAccountDetails = {} as DtoClientAccount;
    matAccountDetails.schools = JSON.parse(JSON.stringify(this.schoolDetailsForm.controls['schools'].value));      
    
    this.isEdit = this.setValueForIsEdit(this.currentStep);

    if(this.currentStep == this.steps.AddSchool){
      let validateOn = "school";

      const isValid = await this.validateJson(matAccountDetails.schools, matAccountDetails, validateOn)
      if(!isValid){
        return;
      }
      this.createIndividualSchools();
    }
    else if(this.currentStep == this.steps.CreateUser){
      this.createSchoolUsers();
    }
    else{
      this.schoolDetailsForm.markAllAsTouched();
      this.toasterService.warning("Please fill the required fields to proceed");
    }
  }

  isFormValidExceptSchoolUsers(): boolean {
    const schools = this.schoolDetailsForm.get('schools') as FormArray;

    // Temporarily disable all schoolUsers controls
    schools.controls.forEach(school => {
      const schoolUsers = school.get('schoolUsers') as FormArray;
      schoolUsers.disable({ emitEvent: false });
    });

    // Check form validity
    const isValid = this.schoolDetailsForm.valid;

    // Re-enable all schoolUsers controls
    schools.controls.forEach(school => {
      const schoolUsers = school.get('schoolUsers') as FormArray;
      schoolUsers.enable({ emitEvent: false });
    });

    return isValid;
  }  

  handleBack(){
    if(this.currentStep == this.steps.CreateUser){
      this.currentStep = this.steps.AddSchool;
      this.maxStep = this.currentStep > this.maxStep ? this.currentStep : this.maxStep;
    }
    this.isEdit = this.setValueForIsEdit(this.currentStep);
  }

  async validateJson(schools: any, matAccountDetails: any, validateOn: string): Promise<boolean> {
    if(this.isFormValidExceptSchoolUsers() || this.schoolDetailsForm.controls['schools']?.valid){
      if(this.hasDuplicates(schools.map((x: any)=> x.schoolName))){
        this.toasterService.warning("School names must be unique");
        return false;
      }
  
      let emails = schools.filter((x: DtoSchool) => x.schoolAdmin && 'emailId' in x.schoolAdmin && x.schoolAdmin.emailId?.trim()).map((x: DtoSchool) => x.schoolAdmin?.emailId?.trim());
      if(this.hasDuplicates(emails)){
        this.toasterService.warning("Email address of school admin(s) must be unique");
        return false;
      }
  
      if(this.roleForm.valid && this.roleForm.value.roleDetails){
        if(this.hasDuplicates(this.roleForm.value.roleDetails.map((x: any)=> x.roleName))){
          this.toasterService.warning("Audience types must be unique");
          return false;
        }
      }
      
      let schoolUserNames: string[] = [];
      let schoolUserEmails: string[] = [];
      for(const element of schools) {
        let school = element;
        school.schoolId = school.schoolId ?? 0;
        school.schoolAdmin.schoolAdminId = school.schoolAdmin.schoolAdminId ?? 0;
        school.schoolAdmin.schoolId = school.schoolAdmin.schoolId ?? 0;
        school.assignedAccountAdmin = school.assignedAccountAdmin?.userId;
        for(const element of school.schoolUsers) {
          let user = element;
          user.schoolUserId = user.schoolUserId ?? 0;
          user.schoolId = school.schoolId ?? 0;
          let fullName = user.firstName?.trim().concat(user.lastName?.trim());
          if(fullName){
            schoolUserNames.push(fullName);
            schoolUserEmails.push(user.emailId);
          }
        }
      }
  
      if(this.hasDuplicates(schoolUserEmails)){
        this.toasterService.warning("Email address of school user(s) must be unique");
        return false;
      } 
    }
    try {
      matAccountDetails.validateOn = validateOn;
      matAccountDetails.isUpdate = this.isEdit;
      const response = await this.matAccountService.validateMatAccount(matAccountDetails);
      if (response.isSuccess) {
        return true;
      } else {
        return false;
      }
    } catch (errorResponse: any) {
      this.toasterService.error(errorResponse.error, (errorResponse.status==422?'Validation Error':'Error'));
      return false;
    }
  }

  resetCreateSchool(){
    this.createIndividualSchoolForm = false;
    this.currentStep = 0; 
    this.schoolUploadFileList = []; 
    this.isEdit = false;
    this.hideHeader.emit(false);
    this.resetSchoolForm();
    this.resetRoleForm();
  }

  async createIndividualSchools(): Promise<any> {
    let form = this.schoolDetailsForm.controls['schools'].value;
    let schoolId: any = 0;
    form.forEach((school: any, index: number) => {
      const schoolName = school.schoolName;
      const adminEmailId = school.schoolAdmin?.emailId;
      schoolId = this.createdSchoolDetails.find(x=>x.schoolName == schoolName)?.schoolId;

      if (school.schoolAccountUploadFileList) {
        if (!this.schoolAccountUploadedFileList[index]) {
          this.schoolAccountUploadedFileList[index] = { id: null, files: [] };
        }
        const files = school.schoolAccountUploadFileList as File[];
        this.schoolAccountUploadedFileList[index].files = files;
        this.schoolAccountUploadedFileList[index].id = schoolName;
      }
    
      if (school.schoolAdminUploadFileLists) {
        if (!this.schoolAdminUploadedFileLists[index]) {
          this.schoolAdminUploadedFileLists[index] = { id: null, files: [] };
        }
        const files = school.schoolAdminUploadFileLists as File[];
        this.schoolAdminUploadedFileLists[index].files = files;
        this.schoolAdminUploadedFileLists[index].id = adminEmailId;
      }
      
      school.assignedAccountAdmin = school.assignedAccountAdmin?.userId;
      school.assignedPackageId = this.packageToAssign;
      school.clientAccountId = this.clientAccountId;
      school.role = this.roleForm.value.roleDetails;
      school.schoolId = schoolId ?? 0;
      school.id = school.id ?? "";
      let schoolAdmin: DtoSchoolAdmin = school.schoolAdmin;
      if(schoolAdmin.emailId && schoolAdmin.firstName && schoolAdmin.lastName && schoolAdmin.contactNumber && schoolAdmin.title){
        schoolAdmin.schoolId = schoolId ?? 0;
        schoolAdmin.schoolAdminId = schoolAdmin.schoolAdminId ?? 0;
        schoolAdmin.countryCode = this.phoneNumberUtil.parse(schoolAdmin.contactNumber?.toString()).getCountryCode()!;
        schoolAdmin.phoneNumber = this.phoneNumberUtil.parse(schoolAdmin.contactNumber?.toString()).getNationalNumber()!;
      }
      else{
        school.schoolAdmin = {} as DtoSchoolAdmin;
      }
      school.schoolUsers = [];
    });
    
    let schools = JSON.parse(JSON.stringify(form));

    if (this.isEdit) {
      this.schoolService.updateSchool(schools).subscribe((response: any) => {
        if (response) {
          if(this.partialSave){
            this.toasterService.success("School details updated successfully!", "Success");
            this.schoolCreated.emit(true);
            this.resetCreateSchool();
            return;
          }
          this.isEdit = false;
          this.schoolService.getSchools(this.clientAccountId).subscribe((data: DtoSchool[] | any)=>{
            this.createdSchoolDetails = data;
            this.currentStep = this.steps.CreateUser;
            this.maxStep = this.currentStep > this.maxStep ? this.currentStep : this.maxStep;
            let payload = {
              isIndividualEntity : this.clientAccountId == 0,
              clientAccountId : this.clientAccountId > 0 ? [this.clientAccountId] : this.createdSchoolDetails.map((x: DtoSchool)=> x.schoolId)
            } as RolePayload;
            this.schoolService.getRoles(payload).subscribe((response: Role[] | any)=>{
            //this.schoolService.getRoles(this.clientAccountId).subscribe((response: Role[] | any)=>{
              this.roles = response;
            },(err: HttpErrorResponse)=>{
              if(err.error){
                this.toasterService.error(err.error, "Error");
              }
            })
          }, (error: HttpErrorResponse)=>{
            if(error.error){
              this.toasterService.error(error.error, "Error");
            }
          })
        }
      }, (errorResponse: HttpErrorResponse) => {
        if(errorResponse.error){
          this.toasterService.error(errorResponse.error, "Error");
        }
      })
    }
    else {
      try {
        //Check if subscription is available for each school's school admin
        let numberOfSubscriptionAvailable = await this.packageService.getAvailableSubscriptionForPackage(this.packageToAssign);
        if(numberOfSubscriptionAvailable < schools.filter((school: DtoSchool)=> school.schoolId == 0 || school.schoolId == undefined || school.schoolId == null).length) {
          this.toasterService.error("Package has exceeded the maximum number of subscriptions");
          return;
        }
      }
      catch (errorResponse: any) {
        this.toasterService.error(errorResponse.error, 'Error');
        return;
      }
      this.schoolService.createSchool(schools, this.clientAccountId).subscribe((response: any) => {
        if (response.isSuccess) {
          this.createdSchoolDetails = response.data;

          this.createdSchoolDetails.forEach((schoolDetail) => {
            // Find the matching school account files by school name
            const matchingSchoolAccountFiles = Object.values(this.schoolAccountUploadedFileList).find(
                item => item.id === schoolDetail.schoolName
            );

            // Find the matching school admin files by admin email ID
            const matchingSchoolAdminFiles = Object.values(this.schoolAdminUploadedFileLists).find(
                item => item.id === schoolDetail.schoolAdmin?.emailId
            );

            if (matchingSchoolAccountFiles && matchingSchoolAccountFiles.files.length > 0) {
              const formData = new FormData();
              matchingSchoolAccountFiles.files.forEach((file) => {
                  formData.append('file', file, file.name);
              });
              this.uploadDocument(formData, DocumentType.School, schoolDetail.entityId!.toString());
            }

            if (matchingSchoolAdminFiles && matchingSchoolAdminFiles.files.length > 0) {
              const formData = new FormData(); 
              matchingSchoolAdminFiles.files.forEach((file) => {                
                formData.append('file', file, file.name);
              });
              this.uploadDocument(formData, DocumentType.EntityAudience, schoolDetail.schoolAdmin?.userId.toString());
            }
          });
          if(this.partialSave){
            this.toasterService.success("School details created successfully!", "Success");
            this.schoolCreated.emit(true);
            this.resetCreateSchool();
            return;
          }

          this.currentStep = this.steps.CreateUser;
          this.maxStep = this.currentStep > this.maxStep ? this.currentStep : this.maxStep;
          let payload = {
            isIndividualEntity : this.clientAccountId == 0,
            clientAccountId : this.clientAccountId > 0 ? [this.clientAccountId] : this.createdSchoolDetails.map((x: DtoSchool)=> x.schoolId)
          } as RolePayload;
          this.schoolService.getRoles(payload).subscribe((response: Role[] | any)=>{
          //this.schoolService.getRoles(this.clientAccountId).subscribe((response: Role[] | any)=>{
            this.roles = response;
          },(err: HttpErrorResponse)=>{
            if(err.error){
              this.toasterService.error(err.error, "Error");
            }
          })
        }
      }, (errorResponse: HttpErrorResponse) => {
        if(errorResponse.error){
          this.toasterService.error(errorResponse.error, "Error");
        }
      })
    }
  }

  async createSchoolUsers(): Promise<any> {
    let schools = JSON.parse(JSON.stringify(this.schoolDetailsForm.controls['schools'].value));
    let form = this.schoolDetailsForm.controls['schools'].value;
    form.forEach((school: any, schoolIndex: number) => {
      const schoolUsers = school.schoolUsers;
  
      // Loop through each school user to assign files and get the relevant email ID
      schoolUsers.forEach((schoolUser: any, schoolUserIndex: number) => {
        const emailId = schoolUser.emailId;
        const key = `${schoolIndex}_${schoolUserIndex}`;
  
        if (schoolUser.UploadedFileList) {
          if (!this.schoolUserUploadedFileLists[key]) {
            this.schoolUserUploadedFileLists[key] = { id: null, files: [] };
          }
  
          // Assign files from the form to the schoolUserUploadedFileLists
          const files = schoolUser.UploadedFileList as File[];
          this.schoolUserUploadedFileLists[key].files = files;
          this.schoolUserUploadedFileLists[key].id = emailId;
        }
      });
    });

    let schoolData: DtoSchool[] = [];
    for(var i=0; i<schools.length; i++) {
      let school: DtoSchool = schools[i];
      let schoolUsersList = school.schoolUsers;
      let savedSchoolData = this.createdSchoolDetails.find(x=>x.schoolName == school.schoolName && x.id == school.id);
      school = savedSchoolData!;
      school.schoolId = savedSchoolData!.schoolId;
      if(school.numberOfSchoolUser == 1){
        this.toasterService.error(`Maximum number of school users for ${school.schoolName} has already been created`);
        return;
      }

      if (school.numberOfSchoolUser!-1 < schoolUsersList.length) {
        this.toasterService.error(`Maximum number of school users for ${school.schoolName} that could be created is ${school.numberOfSchoolUser}`);
        return;
      }

      try {
        //Check if subscription is available for each school's school user
        let numberOfSubscriptionAvailable = await this.packageService.getAvailableSubscriptionForPackage(this.packageToAssign);
        if(numberOfSubscriptionAvailable < schoolUsersList.filter(user => user.schoolUserId == 0 || user.schoolUserId == undefined || user.schoolUserId == null).length) {
          this.toasterService.error("Package has exceeded the maximum number of subscriptions");
          return;
        }
      }
      catch (errorResponse: any) {
        this.toasterService.error(errorResponse.error, 'Error');
        return;
      }
      for(const element of schoolUsersList) {
        let schoolUser: DtoSchoolUser = element;
        schoolUser.countryCode = this.phoneNumberUtil.parse(schoolUser.contactNumber?.toString()).getCountryCode()!;
        schoolUser.phoneNumber = this.phoneNumberUtil.parse(schoolUser.contactNumber?.toString()).getNationalNumber()!;
        schoolUser.schoolUserId = schoolUser.schoolUserId ?? 0;
        schoolUser.schoolId = schoolUser.schoolId ?? 0;
        if(!schoolUser.role.length){
          this.toasterService.warning("Please select at least one role to create a user");
          return;
        }
        school.schoolUsers.push(schoolUser);
      }
      schoolData.push(school);
    }

    this.schoolService.createSchoolUsers(schoolData).subscribe((response: any) => {
      if (response.isSuccess) {       
        this.createIndividualSchoolForm = false;
        this.currentStep = 0;
        this.maxStep = this.currentStep;
        this.resetSchoolForm();
        this.resetRoleForm();
        this.createdSchoolDetails = [];
        this.toasterService.success('School created successfully!', 'Success');
        schoolData.forEach((school: DtoSchool) => {
          // Get school users for the current school
          this.schoolService.getSchoolUsers(school.schoolUsers[0].emailId!, school.schoolId!).subscribe(
            (schoolUserResponse: DtoSchoolUser[] | any) => {
              const schoolUserList = schoolUserResponse;
              schoolUserList.forEach((schooluser: DtoSchoolUser) => {
                // Find the matching school account files by school name
                const matchingSchoolUserFiles = Object.values(this.schoolUserUploadedFileLists).find(
                    item => item.id === schooluser.emailId
                );
    
                if (matchingSchoolUserFiles && matchingSchoolUserFiles.files.length > 0) {
                  const formData = new FormData();
                  matchingSchoolUserFiles.files.forEach((file) => {
                      formData.append('file', file, file.name);
                  });
                  this.uploadDocument(formData, DocumentType.EntityAudience, schooluser.userId.toString());
                }
              });
            },
            (err: HttpErrorResponse) => {
              if(err.error){
                this.toasterService.error(err.error, "Error");
              }
            }
          );
        });
        this.hideHeader.emit(false);
        this.schoolCreated.emit(true);
      }
    }, (errorResponse: HttpErrorResponse) => {
      if(errorResponse.error){
        this.toasterService.error(errorResponse.error, "Error");
      }
    })
  }

  resetSchoolForm() {
    // Reset the form values
    this.schoolDetailsForm.reset();
  
    // Clear the FormArray 'schools'
    const schoolsDetailsArray = this.schoolDetailsForm.get('schools') as FormArray;
    while (schoolsDetailsArray && schoolsDetailsArray.length !== 0) {
      schoolsDetailsArray.removeAt(0); // Remove all controls one by one
    }
    this.sharedService.updateSchoolFormValidity(this.schoolDetailsForm);
  }

  resetRoleForm() {
    // Reset the form values
    this.roleForm.reset();
  
    // Clear the FormArray 'roleDetails'
    const roleDetailsArray = this.roleForm.get('roleDetails') as FormArray;
    while (roleDetailsArray && roleDetailsArray.length !== 0) {
      roleDetailsArray.removeAt(0); // Remove all controls one by one
    }
    this.sharedService.updateRoleFormValidity(this.roleForm);
  }

  hasDuplicates(valuesToCheck: string[]) {
    if(!valuesToCheck){
      return false;
    }
    let mySet = new Set(valuesToCheck);
    if (mySet.size !== valuesToCheck.length) {
      return true;
    }
    else {
      return false;
    }  
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  uploadDocument(formData: FormData, documentTypeId: string, referenceId: string) {
    this.matAccountService.uploadDocument(formData, documentTypeId, referenceId).subscribe(
      (res: any) => {
        if (res) {
          console.log("Uploaded Successfully");
        }
      },
      (error: HttpErrorResponse) => {
        if(error.error){
          this.toasterService.error(error.error, "Error");
        }
      }
    );
  }
}

export enum Steps {
  AddSchool = 1,
  CreateUser = 2
}